import type { NavigateOptions } from '@tanstack/react-router';
import { createContext, useContext } from 'react';
import type { PublicAuthData } from '~/auth/session';
import { useSettingsStore } from '~/util/stores/settings';
import type { Permission } from '~/util/types/permissions';
import type { ToastKey } from '~/util/types/toasts';

export type AuthContextType = {
    auth: Partial<PublicAuthData> | null;
    logout: (showToast: ToastKey, redirectTo: NavigateOptions['to']) => void;
    refreshAuthData: () => Promise<PublicAuthData | null>;
};

export const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
    const context = useContext(AuthContext);

    if (!context) {
        console.error('AuthProvider missing');
        return null;
    }
    return context;
}

export function useAuthData() {
    const { auth } = useAuth() ?? {};
    return auth ?? null;
}

export const useScopes = () => {
    const scopes: Permission[] =
        useAuthData()?.scopes?.filter(
            (scope) => scope != 'submission_priority',
        ) ?? [];
    const { staff_mode } = useSettingsStore();
    const hasScope = (scope: Permission | undefined): boolean => {
        return (
            staff_mode && (scope ? scopes.includes(scope) : scopes.length > 0)
        );
    };

    return {
        scopes,
        hasScope,
    };
};
